import { useState, type FormEvent } from "react";
import type { CloudflareServiceToken } from "../shared/types";
import { Icon } from "./Icon";
import { Modal } from "./Modal";

export function ServiceTokenDialog({
  busy,
  error,
  token,
  onClose,
  onCreate,
  onRotate,
}: {
  busy: boolean;
  error: string;
  token?: CloudflareServiceToken;
  onClose: () => void;
  onCreate: (name: string, duration: string) => void;
  onRotate: (token: CloudflareServiceToken, graceSeconds: number) => void;
}) {
  const rotating = Boolean(token);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("8760h");
  const [graceSeconds, setGraceSeconds] = useState(86_400);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (token) onRotate(token, graceSeconds);
    else onCreate(name.trim(), duration);
  }

  return (
    <Modal
      closeDisabled={busy}
      eyebrow="Machine identity"
      onClose={onClose}
      title={rotating ? `Rotate ${token?.name}` : "Create service token"}
      titleId="service-token-dialog-title"
    >
      <form className="dialog__form" onSubmit={submit}>
        <div className="dialog__body service-token-form">
          {rotating ? (
            <>
              <div className="notice notice--warning">
                The new secret is shown once. During the overlap, both the old
                and new secrets can authenticate.
              </div>
              <label>
                <span>Keep previous secret valid</span>
                <select
                  disabled={busy}
                  onChange={(event) =>
                    setGraceSeconds(Number(event.target.value))
                  }
                  value={graceSeconds}
                >
                  <option value={0}>Revoke immediately</option>
                  <option value={3600}>1 hour</option>
                  <option value={86400}>24 hours</option>
                  <option value={604800}>7 days</option>
                  <option value={2592000}>30 days</option>
                </select>
              </label>
            </>
          ) : (
            <>
              <label>
                <span>Name</span>
                <input
                  autoComplete="off"
                  data-autofocus
                  disabled={busy}
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="CI deploy agent"
                  required
                  value={name}
                />
              </label>
              <label>
                <span>Validity</span>
                <select
                  disabled={busy}
                  onChange={(event) => setDuration(event.target.value)}
                  value={duration}
                >
                  <option value="720h">30 days</option>
                  <option value="2160h">90 days</option>
                  <option value="8760h">1 year</option>
                  <option value="17520h">2 years</option>
                  <option value="forever">Never expires</option>
                </select>
              </label>
            </>
          )}
          {error && (
            <div className="notice notice--error" role="alert">
              {error}
            </div>
          )}
        </div>
        <footer className="dialog__actions">
          <button
            className="button button--secondary"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="button button--primary"
            disabled={busy}
            type="submit"
          >
            <Icon name="rotate" size={16} />
            {busy ? "Working…" : rotating ? "Rotate secret" : "Generate token"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
